import classNames from "classnames";
import { ButtonPrimary } from "components/Clickable/Buttons";
import { ErrorMessage } from "components/Input/ErrorMessage";
import { TextInputGroup } from "components/Input/Input";
import { writeCMSDataClientSide } from "helpers/api";
import { processServerSideErrors } from "helpers/forms";
import { FormBase } from "interfaces";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import {
    AccountOverviewList,
    FavoriteItemsInNewList,
    PDPFavoriteItemApi,
    RulesetFavoriteItemApi,
} from "./interfaces";

import styles from "./new-list.module.scss";

interface NewListProps {
    ctaText?: string;
    placeholder?: string;
    className?: string;
    request: PDPFavoriteItemApi | RulesetFavoriteItemApi | null;
    createFirstListImmediately?: boolean;
    onSuccess: (list: AccountOverviewList) => void;
}

interface Form extends FormBase {
    title: string;
}

export const NewList = ({
    className,
    placeholder,
    ctaText = "Create",
    request,
    createFirstListImmediately = false,
    onSuccess,
}: NewListProps) => {
    const titleMaxLength = 256;

    const {
        clearErrors,
        register,
        handleSubmit,
        setError,
        resetField,
        formState: { errors, isSubmitting },
    } = useForm<Form>();

    const createList: SubmitHandler<Form> = async (data) => {
        const newListRequest: FavoriteItemsInNewList = {
            title: data.title,
            items: !!request ? [{ ...request, lists: [] }] : [],
        };

        const response = await writeCMSDataClientSide(
            "POST",
            "/api/favorites/",
            {},
            newListRequest,
        );
        if (response.ok) {
            onSuccess(await (response.json() as Promise<AccountOverviewList>));
            resetField("title");
        } else {
            processServerSideErrors(response, data, setError);
        }
    };

    useEffect(() => {
        if (createFirstListImmediately) {
            createList({
                title: "My List",
                server: null,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <section className={classNames([styles.newList, className])}>
            {createFirstListImmediately ? (
                <p>Creating a new list for your item…</p>
            ) : (
                <form
                    onSubmit={(event) => {
                        clearErrors();
                        handleSubmit(createList)(event);
                    }}
                    noValidate
                >
                    <TextInputGroup
                        {...register("title", {
                            required: {
                                value: true,
                                message: "Enter a name for your new list.",
                            },
                            maxLength: {
                                value: titleMaxLength,
                                message: `List titles cannot be longer than ${titleMaxLength} characters.`,
                            },
                        })}
                        type="text"
                        label="Create a new list"
                        placeholder={placeholder}
                        errorMessage={errors.title?.message}
                    />
                    <ButtonPrimary
                        className="al-fav__create"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        type="submit"
                    >
                        {ctaText}
                    </ButtonPrimary>
                    {errors.server?.message && (
                        <ErrorMessage>{errors.server?.message}</ErrorMessage>
                    )}
                </form>
            )}
        </section>
    );
};
